import { useCallback, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import './styles/global.css';
import Background    from './components/Background';
import Login         from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import StaffRegister from './pages/StaffRegister';
import StaffWebviewBootstrap from './pages/StaffWebviewBootstrap';
import GroupSelect   from './pages/GroupSelect';
import DismissalBoard from './pages/DismissalBoard';
import DriverInvite   from './pages/DriverInvite';
import MobileAppRedirect from './pages/MobileAppRedirect';
import { apiFetch, BASE_URL, SOCKET_URL } from './config/api';
import { clearStoredUser, loadStoredUser, saveStoredUser } from './lib/authStorage';
import {
  setReplayScreen,
  startWebReplayTracking,
  stopWebReplayTracking,
  trackReplayEvent,
} from './lib/replayTracker';

// Contacts (parent/guardian/driver) belong in the mobile app, not the staff webapp.
const CONTACT_TYPES = new Set(['parent', 'guardian', 'driver']);
function isContactUser(user) {
  return Boolean(user && CONTACT_TYPES.has(user.type));
}

function normalizeLockdownActive(value) {
  return Boolean(Number(value));
}

function getInitialResetCode() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('code') || '';
}

// Public driver-invite link: /d/:token (no login required).
function getDriverInviteToken() {
  if (typeof window === 'undefined') return '';
  const match = window.location.pathname.match(/^\/d\/([^/]+)\/?$/);
  if (match) return decodeURIComponent(match[1]);
  return '';
}

// Public staff registration page: /staff-register or /staff-register/:schoolId.
function getStaffRegistrationSchoolId() {
  if (typeof window === 'undefined') return null;
  const pathMatch = window.location.pathname.match(/^\/staff-register(?:\/([^/]+))?\/?$/);
  if (pathMatch) return pathMatch[1] ? decodeURIComponent(pathMatch[1]) : '';

  if (window.location.pathname.replace(/^\/+|\/+$/g, '') === 'register') {
    return new URLSearchParams(window.location.search).get('school_id') || '';
  }

  return null;
}

// Native staff app WebView handoff: /staff-webview?token=<one-time-token>.
function getStaffWebviewBootstrapParams() {
  if (typeof window === 'undefined') return null;
  const cleanedPath = window.location.pathname.replace(/^\/+|\/+$/g, '');
  if (cleanedPath !== 'staff-webview') return null;

  const token = new URLSearchParams(window.location.search).get('token') || '';
  return { token };
}

function getInitialPage() {
  if (typeof window !== 'undefined') {
    const cleanedPath = window.location.pathname.replace(/^\/+|\/+$/g, '');
    if (cleanedPath === 'reset-password') return 'reset';
  }

  return loadStoredUser() ? 'group-select' : 'login';
}

function LockdownView({ onRefresh, onLogout }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        className="glass-card"
        style={{
          width: 'min(100%, 560px)',
          padding: '40px 32px',
          textAlign: 'center',
          color: '#fff',
        }}
      >
        <div style={{ fontSize: '3rem', fontWeight: 900, lineHeight: 0.95, color: '#ff8a5b' }}>
          LOCKDOWN ACTIVE
        </div>
        <p style={{ marginTop: 20, color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
          Follow established school lockdown procedures.
          <br />
          Student dismissal has been suspended until further notice.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 28, flexWrap: 'wrap' }}>
          <button className="btn-primary" onClick={onRefresh}>Refresh</button>
          <button className="btn-secondary" onClick={onLogout}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser]   = useState(() => loadStoredUser());
  const [page, setPage]   = useState(getInitialPage); // login | register | reset | group-select | board
  const [resetCode] = useState(getInitialResetCode);
  const [driverInviteToken] = useState(getDriverInviteToken);
  const [staffWebviewBootstrap, setStaffWebviewBootstrap] = useState(getStaffWebviewBootstrapParams);
  const staffRegistrationSchoolId = getStaffRegistrationSchoolId();
  const [lockdownActive, setLockdownActive] = useState(false);
  const [mobileOnlyName, setMobileOnlyName] = useState(null);
  const [selectedGrades, setSelectedGrades] = useState([]);

  const refreshLockdownState = useCallback(async (activeUser = user) => {
    if (!activeUser?.token || !activeUser?.school_id) {
      setLockdownActive(false);
      return;
    }

    try {
      const res = await apiFetch(`/schools/${activeUser.school_id}`, {}, activeUser.token);
      setLockdownActive(normalizeLockdownActive(res?.data?.lockdown_active));
    } catch {
      // leave current state untouched on transient failures
    }
  }, [user]);

  const handleLogin = async (u) => {
    // Parents/guardians/drivers can't use the staff dashboard — point them to the app.
    if (isContactUser(u)) {
      setMobileOnlyName(u.name || null);
      return;
    }

    const normalizedUser = {
      ...u,
      school_id: u.school_id ?? u.school_ids?.[0] ?? null,
    };

    // Fetch school name to display in header
    if (normalizedUser.school_id) {
      try {
        const res = await apiFetch(`/schools/${normalizedUser.school_id}`, {}, normalizedUser.token);
        const school = res.data;
        normalizedUser.school_name = school?.school_nickname || school?.legal_name || '';
      } catch { /* non-critical */ }
    }

    saveStoredUser(normalizedUser);
    setUser(normalizedUser);
    setPage('group-select');
    refreshLockdownState(normalizedUser);
  };
  const handleStaffWebviewComplete = useCallback((u) => {
    const normalizedUser = {
      ...u,
      school_id: u.school_id ?? u.school_ids?.[0] ?? null,
    };

    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', '/');
    }

    saveStoredUser(normalizedUser);
    setUser(normalizedUser);
    setSelectedGrades([]);
    setPage('group-select');
    setStaffWebviewBootstrap(null);
    refreshLockdownState(normalizedUser);
  }, [refreshLockdownState]);

  const handleLogout  = ()   => { stopWebReplayTracking(); clearStoredUser(); setUser(null); setPage('login'); setLockdownActive(false); setMobileOnlyName(null); };
  const handleRegister = ()  => setPage('register');
  const handleBack    = ()   => setPage('login');
  const handleViewBoard = (grades) => { setSelectedGrades(grades); setPage('board'); };

  useEffect(() => {
    refreshLockdownState();
  }, [refreshLockdownState]);

  // Record this teacher's session for carpool replay. Restarting on token or
  // school change keeps events attributed to the right account.
  useEffect(() => {
    if (!user?.token || !user?.school_id) return undefined;

    startWebReplayTracking({
      baseUrl: BASE_URL,
      token: user.token,
      app: 'webapp',
      schoolId: user.school_id,
      screen: page,
    });

    return () => stopWebReplayTracking();
  }, [user?.token, user?.school_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Page changes are the webapp's equivalent of navigation.
  useEffect(() => {
    setReplayScreen(page);
  }, [page]);

  useEffect(() => {
    if (!user?.school_id) return undefined;

    const socket = io(SOCKET_URL);
    socket.on('connect', () => {
      socket.emit('watch-school', user.school_id);
      refreshLockdownState();
      trackReplayEvent('socket.connect', { url: SOCKET_URL });
    });
    socket.on('disconnect', (reason) => {
      trackReplayEvent('socket.disconnect', { reason });
    });
    socket.on('school-lockdown-changed', (payload = {}) => {
      if (payload.school_id && Number(payload.school_id) !== Number(user.school_id)) return;
      setLockdownActive(normalizeLockdownActive(payload.lockdown_active));
    });

    return () => socket.disconnect();
  }, [refreshLockdownState, user?.school_id]);

  // Native staff-app WebView bootstrap — one-time token, bypasses stored auth.
  if (staffWebviewBootstrap) {
    return (
      <Background>
        <StaffWebviewBootstrap
          token={staffWebviewBootstrap.token}
          onComplete={handleStaffWebviewComplete}
        />
      </Background>
    );
  }

  // Public driver-invite page — no login, bypasses auth/lockdown
  if (driverInviteToken) {
    return (
      <Background>
        <DriverInvite token={driverInviteToken} />
      </Background>
    );
  }

  // Public staff registration page — no login, bypasses auth/lockdown
  if (staffRegistrationSchoolId !== null) {
    return (
      <Background>
        <StaffRegister
          schoolId={staffRegistrationSchoolId}
          onBack={() => { window.location.href = '/'; }}
        />
      </Background>
    );
  }

  // Parent/guardian/driver tried to sign in — staff dashboard isn't for them.
  if (mobileOnlyName !== null || isContactUser(user)) {
    return (
      <Background>
        <MobileAppRedirect name={mobileOnlyName || user?.name} onBack={handleLogout} />
      </Background>
    );
  }

  // Auth / register screens — wrapped in illustrated background
  if (page === 'reset') {
    return (
      <Background>
        <ResetPassword onBack={handleBack} initialCode={resetCode} />
      </Background>
    );
  }

  if (!user) {
    return (
      <Background>
        {page === 'register'
          ? <StaffRegister onBack={handleBack} />
          : <Login onLogin={handleLogin} onRegister={handleRegister} onReset={() => setPage('reset')} />
        }
      </Background>
    );
  }

  if (lockdownActive) {
    return (
      <Background>
        <LockdownView onRefresh={() => refreshLockdownState()} onLogout={handleLogout} />
      </Background>
    );
  }

  // Authenticated — full-screen pages still use the background
  return (
    <Background>
      {page === 'group-select' && (
        <GroupSelect user={user} onLogout={handleLogout} onViewBoard={handleViewBoard} />
      )}
      {page === 'board' && (
        <DismissalBoard user={user} onLogout={handleLogout} selectedGrades={selectedGrades} onBack={() => setPage('group-select')} />
      )}
    </Background>
  );
}
